import {
  BookOpenIcon,
  ClipboardListIcon,
  CalendarIcon,
  BarChart2Icon,
  TrophyIcon,
  SearchIcon,
  ShuffleIcon,
  SmartphoneIcon,
  BrainIcon,
  ZapIcon,
  CrownIcon,
  TrendingUpIcon,
  CheckCircle2Icon,
} from 'lucide-react'

export const pageData = {
  global: {
    productName: 'BASEBALL READY: SYSTEM 365',
    price: 27,
    originalPrice: 97,
    currency: 'USD',
    guaranteeDays: 7,
    checkoutUrl: 'https://baseball-ready.impultienda.ar/checkout',
  },
  hero: {
    badge: 'PROVEN COACHING SYSTEM',
    headline: {
      part1: 'THE COMPLETE SEASON PLANNING SYSTEM FOR ',
      highlight: 'BASEBALL COACHES',
    },
    subheadline:
      'Save time, reduce stress, and develop better players — without spending hours planning training sessions from scratch.',
    ctaText: 'GET MY SYSTEM NOW →',
    ratingText: '4.9/5 — Verified Reviews · 7-Day Money Back Guarantee',
    features: [
      { icon: BookOpenIcon, text: '300+ PAGES OF COACHING RESOURCES' },
      { icon: ClipboardListIcon, text: 'READY-TO-USE SESSIONS FOR EVERY AGE' },
      { icon: CalendarIcon, text: 'SEASON PLANNING TOOLS' },
      { icon: BarChart2Icon, text: 'PLAYER EVALUATION SYSTEMS' },
      { icon: TrophyIcon, text: 'DEVELOP BETTER PLAYERS & WIN MORE GAMES' },
    ],
  },
  painPoints: {
    heading: 'DOES THIS SOUND FAMILIAR?',
    intro:
      'You love coaching baseball, but planning sessions is eating up your free time. Most coaches and PE teachers face one of these:',
    items: [
      {
        emoji: '😰',
        icon: SearchIcon,
        title: 'CREATIVE BURNOUT',
        description:
          'Hours lost searching online for drills that work — only to end up more confused.',
      },
      {
        emoji: '🍃',
        icon: ShuffleIcon,
        title: 'LACK OF STRUCTURE',
        description:
          'Practices lose their flow, and you wonder if your players are really improving.',
      },
      {
        emoji: '📱',
        icon: SmartphoneIcon,
        title: 'INFORMATION OVERLOAD',
        description:
          'Endless conflicting advice online, and no clear system that actually works.',
      },
      {
        emoji: '😩',
        icon: BrainIcon,
        title: 'MASSIVE MENTAL LOAD',
        description:
          'Managing kids, gear, and games leaves you drained before the first pitch.',
      },
    ],
  },
  solution: {
    badge: 'A COMPLETE 6-VOLUME BASEBALL SYSTEM',
    heading: "THE SOLUTION YOU'VE BEEN WAITING FOR",
    subheading: 'DESIGNED FOR PE TEACHERS & YOUTH COACHES',
    description:
      'BASEBALL READY: SYSTEM 365 is much more than a manual. It is a complete Coaching Operating System that gives you structured player development, ready-to-use sessions, and tactical mastery.',
    highlights: [
      '✓ EVERYTHING ORGANIZED',
      '✓ EVERYTHING STRUCTURED',
      '✓ READY TO USE ON YOUR MOBILE',
    ],
  },
  volumes: {
    heading: 'THE COMPLETE 6-VOLUME SYSTEM',
    items: [
      {
        volume: 'VOLUME 1',
        title: 'THE FOUNDATION BLUEPRINT',
        subtitle: 'Mechanics from day one',
        description:
          'Build elite mechanics from day one. Throwing, catching, and the "Ready Position" mastered.',
      },
      {
        volume: 'VOLUME 2',
        title: 'OFFENSIVE POWERHOUSE',
        subtitle: 'The step-by-step swing',
        description:
          'Stance, load, and contact. Turn your lineup into a scoring machine.',
      },
      {
        volume: 'VOLUME 3',
        title: 'INFIELD & OUTFIELD MASTERY',
        subtitle: 'Defense by position',
        description:
          'Defensive fundamentals by position. Footwork, tracking fly balls, and impenetrable defense.',
      },
      {
        volume: 'VOLUME 4',
        title: 'BASE RUNNING & IQ',
        subtitle: 'Steal runs with intelligence',
        description:
          'The "Banana Turn," sliding safety, and game-winning strategy.',
      },
      {
        volume: 'VOLUME 5',
        title: 'THE 365 PRACTICE VAULT',
        subtitle: '30 scripted sessions',
        description:
          'Minute-by-minute plans for a full year of elite training.',
      },
      {
        volume: 'VOLUME 6',
        title: "ELITE COACH'S MANAGEMENT",
        subtitle: 'Professional logistics',
        description: 'Evaluations, gameday templates, and leadership tools.',
      },
    ],
  },
  lookInside: {
    heading: 'LOOK INSIDE THE SYSTEM',
    description:
      'See exactly what you get — Mobile-ready Vertical PDFs designed for quick field use.',
    previewImages: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/bYDN36AFWHLyhjCMya62xc/Captura-de-pantalla-2026-06-18-a-las-11.49.53_p._m..webp',
        alt: 'Drill #1 — The Statue: Balance for Pitching Mechanics',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/whexnPxg6ob98kwkx5WpFX/Captura_de_pantalla_2026-06-18_a_las_11.55.05_p.webp',
        alt: 'The Double Play — Shortstop Perspective',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/5zzNUZa8yAiKABGxd1HMNM/Captura_de_pantalla_2026-06-18_a_las_11.56.28_p.webp',
        alt: 'Leads & Primary Footwork — 1st Base',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/68MhcYudqiL2qHNxtxNJHU/Captura_de_pantalla_2026-06-18_a_las_11.58.19_p.webp',
        alt: 'Station-Based Circuit Training',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/8ZhDMr5D4roLM3QWy7HJkj/Captura-de-pantalla-2026-06-18-a-las-11.50.47_p._m..webp',
        alt: 'Indoor Drill — Wiffle Ball Precision',
      },
    ],
    previewCaption:
      'Real sample pages from the system — designed for the field.',
  },
  bonuses: {
    heading: '5 BONUSES INCLUDED (FREE TODAY)',
    subheading:
      'Exclusive coaching resources that will transform your workflow.',
    items: [
      {
        id: 1,
        value: '$47',
        image:
          'https://cdn.magicpatterns.com/uploads/ieGM8Dcr7dZ8ffhgAvWadV/98812e78-49a7-46ab-8b2a-b68de1490d6d.webp',
        title: '52 READY-TO-USE WARM-UPS',
        bullets: [
          'Dynamic activation for every week of the year.',
          'Stop standing still. Start moving with purpose.',
        ],
      },
      {
        id: 2,
        value: '$37',
        image:
          'https://cdn.magicpatterns.com/uploads/qpHCe1oza9Kdgk6gi4iA73/b4a43b9d-7dba-48f6-bf5f-7b9414c543d7.webp',
        title: '100-DRILL VISUAL ENCYCLOPEDIA',
        bullets: [
          'A massive library of drills for every skill.',
          'Never run out of ideas — ready in your pocket.',
        ],
      },
      {
        id: 3,
        value: '$27',
        image:
          'https://cdn.magicpatterns.com/uploads/5YXkW78rsMDNBn9ZwpsdHe/b9b1639c-81b2-44b6-90a9-aea757ca315b.webp',
        title: 'THE "RAINY DAY" INDOOR MANUAL',
        bullets: [
          'Field unplayable? No problem.',
          '20 gym-ready sessions to keep the season moving.',
        ],
      },
      {
        id: 4,
        value: '$19',
        image:
          'https://cdn.magicpatterns.com/uploads/4Bjc82wgzKjbaD4XGFk1Gi/d073de47-6233-4263-874e-3cf0284110fb.webp',
        title: 'COACH COMMANDS HANDBOOK',
        bullets: [
          '100+ verbal cues to fix mechanics fast.',
          'Know exactly what to say in 2 words or less.',
        ],
      },
      {
        id: 5,
        value: '$29',
        image:
          'https://cdn.magicpatterns.com/uploads/jPBnEuWHfotqBhUAJKwKoQ/b233ccff-0258-4afd-a119-19a8e97fae69_(1).webp',
        title: 'PLAYER EVALUATION SYSTEM',
        bullets: [
          'Professional player assessment tools.',
          'Track progress. Create reports. Build development plans.',
        ],
      },
    ],
    extraCallout: 'EXTRA FREE BONUS: BASEBALL READY: SYSTEM 365',
  },
  benefits: {
    heading: 'WHAT WILL CHANGE?',
    intro: 'What will change in your coaching life?',
    items: [
      {
        title: 'FREEDOM FROM PLANNING STRESS',
        description:
          'Open the PDF. Choose the plan. Run the session. The hard work is already done for you.',
      },
      {
        title: 'AUTHORITY AND CONFIDENCE',
        description:
          'With a proven structure, you arrive at the field as the expert. Your players (and parents) will notice the difference instantly.',
      },
      {
        title: 'REAL PLAYER DEVELOPMENT',
        description:
          'No more guessing. Structured progressions mean your players improve faster, stay safer, and have more fun.',
      },
      {
        title: 'MOBILE-FIRST CONVENIENCE',
        description:
          'Designed to be read on your phone while walking the field. Zero-prep. Zero-paperwork.',
      },
    ],
  },
  specialOffer: {
    pill: 'SPECIAL LAUNCH OFFER · 72% OFF',
    heading: 'THE COMPLETE 6-VOLUME SYSTEM',
    paymentType: 'Pay securely in your local currency.',
    ctaText: 'GET THE SYSTEM — USD 27 →',
  },
  guarantee: {
    heading: '7-DAY FULL GUARANTEE',
    text: "Your money is completely protected. You have 7 full days to explore all 6 volumes, use the sessions, and test the bonuses. If you're not completely satisfied that this will save you hours of work, simply request a refund and we'll return 100% of your purchase.",
    subtext: 'Zero Risk. Your investment is 100% protected.',
  },
  testimonials: {
    heading: 'WHAT OTHER COACHES ARE SAYING',
    items: [
      {
        quote:
          "I'm a PE teacher with limited baseball experience. This system made me look like a pro. The lesson plans in Volume 5 are a lifesaver!",
        name: 'Mark S.',
        role: 'California PE Teacher',
      },
      {
        quote:
          "The Baserunning IQ volume alone is worth the price. We stopped making silly mistakes on the bases and started winning. Truly 'Ready-to-Use'.",
        name: 'Coach David',
        role: 'Youth League',
      },
    ],
  },
  faq: {
    heading: 'FREQUENTLY ASKED QUESTIONS',
    items: [
      {
        question: 'Is this a physical or digital product?',
        answer:
          'Baseball Ready is a 100% digital product delivered in PDF format. Access it instantly on your phone, tablet, or laptop.',
      },
      {
        question: 'Is it for beginners or pros?',
        answer:
          'It\'s for both. It provides the "Foundation" for beginners and the "Management/IQ" tools for experienced coaches.',
      },
      {
        question: 'Can I print the materials?',
        answer:
          'Absolutely. Each volume and bonus is formatted for high-quality printing if you prefer a physical binder.',
      },
    ],
  },
  lastChance: {
    heading: 'LAST CHANCE',
    subheading:
      'Your players deserve a coach who is prepared, confident, and focused — not someone guessing their way through a practice session. Stop spending your evenings on YouTube. Stop building practices from scratch. Take control of your season today.',
    ctaText: 'GET BASEBALL READY NOW →',
  },
  footer: {
    copyright: '© 2026 Baseball Ready: System 365 — All Rights Reserved.',
    disclaimer:
      'Baseball Ready: System 365 is a coaching resource. Please follow local safety and safeguarding regulations.',
    links: [
      { label: 'Terms & Conditions', href: '#' },
      { label: 'Privacy Policy', href: '#' },
      { label: 'Contact', href: '#' },
    ],
  },
}
